import { colors } from '@kmns-internal/furikake'
import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import { useLayoutEffect, useRef } from 'react'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import type { TableActionMenuItem } from '../TableActionMenu'
import type {
  TableBranchColumn,
  TableDisplayColumn,
  TableHeaderBandCell,
} from '../model'
import { TablePreviewFixedRow } from './TablePreviewFixedRow'
import { TablePreviewHeader } from './TablePreviewHeader'

/** 編集モードで preview が必要とする操作ハンドラ一式。読み取り専用では渡さない。 */
export type TableEditHandlers = {
  /** 列 / 分岐質問の見出しをタップして選択する(設定パネルに反映)。 */
  onSelectColumn: (questionId: number) => void
  /** 並び替え。引数は列の index(分岐は列ごとブロック移動する)。 */
  onReorderColumn: (fromIndex: number, toIndex: number) => void
  /** 列ヘッダーの操作メニュー。 */
  buildColumnMenuItems: (column: TemplateNodeSchema) => TableActionMenuItem[]
  /** 分岐質問ヘッダーの操作メニュー。 */
  buildBranchMenuItems: (branch: TableBranchColumn) => TableActionMenuItem[]
  /** 分岐質問が未追加の条件分岐へ最初の分岐質問を追加する。 */
  onAddBranchQuestion: (logicId: number) => void
  /** 条件分岐を分岐質問ごと削除する。 */
  onDeleteLogic: (band: TableHeaderBandCell) => void
  /** 行の操作メニュー。 */
  buildRowMenuItems: (rowId: number) => TableActionMenuItem[]
  /** 見出し列の列見出し(テンプレート行)またはセル(固定行)をタップして選択する。 */
  onSelectRowHeading: (rowId: number) => void
  onToggleRowHeadingHidden: () => void
}

type BaseProps = {
  displayColumns: TableDisplayColumn[]
  /** テンプレート行(`isRepeat = 1`)。名前が行見出し列の列名になる。 */
  templateRow: TemplateNodeSchema
  rowHeadingHidden: boolean
  rows: TemplateNodeSchema[]
  templateNodes: TemplateNodesDict
  activeNodeId: number
}

/** 編集モード。列・行の操作系は編集時のみ意味を持つため、ここで必須にする。 */
type InteractiveProps = BaseProps & {
  interactive: true
  showAddColumn: boolean
  onAddColumn: () => void
  handlers: TableEditHandlers
}

/** 読み取り専用(プレビュー)モード。操作系コールバックは受け取らない。 */
type ReadonlyProps = BaseProps & {
  interactive?: false
}

type Props = InteractiveProps | ReadonlyProps

export const TablePreview = (props: Props) => {
  const { displayColumns, rows, templateNodes, activeNodeId } = props
  const addColumnButtonRef = useRef<HTMLButtonElement>(null)
  const shouldScrollAfterAppendRef = useRef(false)

  useLayoutEffect(() => {
    if (!shouldScrollAfterAppendRef.current) return

    shouldScrollAfterAppendRef.current = false
    addColumnButtonRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'end',
    })
  }, [displayColumns.length])

  const handleAddColumn = () => {
    if (!props.interactive) return

    shouldScrollAfterAppendRef.current = true
    props.onAddColumn()
  }

  const headerProps = props.interactive
    ? { interactive: true as const, handlers: props.handlers }
    : { interactive: false as const }

  const table = (
    // 列ドラッグ時の差し込み線(ヘッダーの ::after)を本体行まで貫かせ、下端をここでクリップする。
    // ラッパは max-content でテーブルにぴったり沿うので、線はテーブル下端で切れる。
    // clip なのは、hidden だとこのラッパ自体がスクロールコンテナになり、
    // 中の position: sticky が横スクロールに追従しなくなるため。
    <Box sx={{ position: 'relative', width: 'max-content', overflow: 'clip' }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          width: 'max-content',
          bgcolor: colors.white,
        }}
      >
        <TablePreviewHeader
          displayColumns={displayColumns}
          templateRow={props.templateRow}
          rowHeadingHidden={props.rowHeadingHidden}
          activeNodeId={activeNodeId}
          {...headerProps}
        />
        <Box component="tbody">
          {rows.map(row => {
            const rowProps = props.interactive
              ? {
                  interactive: true as const,
                  onSelectColumn: props.handlers.onSelectColumn,
                  onSelectRowHeading: props.handlers.onSelectRowHeading,
                  rowMenuItems: props.handlers.buildRowMenuItems(row.id),
                }
              : { interactive: false as const }
            return (
              <TablePreviewFixedRow
                key={row.id}
                row={row}
                rowHeadingHidden={props.rowHeadingHidden}
                displayColumns={displayColumns}
                templateNodes={templateNodes}
                activeNodeId={activeNodeId}
                {...rowProps}
              />
            )
          })}
        </Box>
      </Box>
    </Box>
  )

  if (props.interactive && props.showAddColumn) {
    return (
      // 行を内容ぴったりの幅にする。100% 幅の flex 行だと table(max-content)が縮められ、
      // 親の overflow が効かず横スクロールできなくなるため。
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        sx={{ width: 'max-content' }}
      >
        {table}
        <Button
          ref={addColumnButtonRef}
          variant="text"
          startIcon={<AddIcon />}
          onClick={handleAddColumn}
          aria-label="列を追加する"
          sx={theme => ({
            height: theme.spacing(6),
            whiteSpace: 'nowrap',
            flexShrink: 0,
          })}
        >
          追加する
        </Button>
      </Stack>
    )
  }

  return table
}
